import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Summary } from '@features/opportunities/models/opportunity';
import { OpportunityRequirement } from '@features/opportunities/modules/core/models/schema-property-definition';
import {
  OpportunityMissingRequirements,
  RequirementService
} from '@features/opportunities/modules/core/services/requirement.service';
import { Violation } from '@features/opportunities/modules/core/services/violation.contract';
import { ViolationService } from '@features/opportunities/modules/core/services/violation.service';
import { RequirementModeService } from '@features/opportunities/services/requirement-mode.service';
import { isEmptyObject } from '@shared/utilities/typescript-utils';

import {
  ApiHttp,
  HttpAttachmentOptions,
  HttpOptions,
  SCHEMA_VERSION
} from './api-http';

/**
 * TODO: Make use of OpportunityRequirementOptions
 */
interface ResponseWithRequirementsAndViolations<T> {
  data: T & {
    summary: Summary;
  };
  requirementMode: OpportunityRequirement.Mode;
  requirements: OpportunityMissingRequirements;
  violations: Violation[];
}

export interface OpportunityApiHttpFeatures {
  withRequirementsAndViolations: boolean;
  withOpportunitySummary: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OpportunityApiHttp {
  constructor(
    private http: ApiHttp,
    private violationService: ViolationService,
    private requirementMode: RequirementModeService,
    private readonly requirementService: RequirementService
  ) {}

  public static features(
    withViolations: boolean,
    withSummary: boolean = false
  ): OpportunityApiHttpFeatures {
    return {
      withRequirementsAndViolations: withViolations,
      withOpportunitySummary: withSummary
    };
  }

  public getWithFeatures<T>(
    url: string,
    options: HttpOptions = ApiHttp.getStandardOptions(),
    features: OpportunityApiHttpFeatures = OpportunityApiHttp.features(
      false,
      false
    )
  ): Observable<T> {
    let optionsToSend = { ...options };

    let checkAndAddParam = (name: string, value: boolean) => {
      if (value) {
        if (optionsToSend.params == undefined || optionsToSend.params == null) {
          optionsToSend.params = {
            [name]: true
          };
        } else {
          optionsToSend.params[name] = true;
        }
      }
    };

    checkAndAddParam(
      'requirementsAndViolations',
      features.withRequirementsAndViolations
    );

    // Now that OpporrtunityType is no longer in the schema, we want to add opportunitySummary to all requests
    // so that we can access it from state
    checkAndAddParam('opportunitySummary', true);

    return features.withRequirementsAndViolations
      ? this.http
          .getEntity<ResponseWithRequirementsAndViolations<T>>(
            environment.opportunityApiUrl,
            url,
            SCHEMA_VERSION.OPPORTUNITY,
            optionsToSend
          )
          .pipe(
            tap((x) => {
              let options = {
                opportunityType: x.data.summary.opportunityType,
                requirements: x.requirements,
                requirementMode: x.requirementMode
              };
              this.requirementService.update(options);
            }),
            tap((x) => this.requirementMode.updateMode(x.requirementMode)),
            tap((x) => this.violationService.updateViolations(x.violations)),
            tap((x) =>
              this.requirementMode.updateRequirements(
                x.requirements,
                x.data.summary.opportunityType
              )
            ),
            map((x) => x.data)
          )
      : this.http.getEntity<T>(
          environment.opportunityApiUrl,
          url,
          SCHEMA_VERSION.OPPORTUNITY,
          optionsToSend
        );
  }

  public getLendersForHomeLoan(): Observable<{ id: string; name: string }[]> {
    return this.get<{ id: string; name: string }[]>('tools/lenders', {
      ...ApiHttp.getStandardOptions(),
      params: { opportunityTypes: 'Home' }
    });
  }

  public getWithMetadata<T>(path: string): Observable<T> {
    return this.http.getWithMetadata(
      environment.opportunityApiUrl,
      path,
      SCHEMA_VERSION.OPPORTUNITY
    );
  }

  public getText(path: string): Observable<string> {
    return this.http.getText(
      environment.opportunityApiUrl,
      path,
      SCHEMA_VERSION.OPPORTUNITY
    );
  }

  public get<T>(path: string, options?: HttpOptions): Observable<T> {
    return this.http.getEntity(
      environment.opportunityApiUrl,
      path,
      SCHEMA_VERSION.OPPORTUNITY,
      options
    );
  }

  public post<T>(
    path: string,
    body: any,
    options: HttpOptions | HttpAttachmentOptions
  ): Observable<T> {
    return this.http.postEntity(
      environment.opportunityApiUrl,
      path,
      body,
      SCHEMA_VERSION.OPPORTUNITY,
      options
    );
  }

  public put(
    path: string,
    body: any,
    options: HttpOptions
  ): Observable<object> {
    if (isEmptyObject(body)) {
      return of({});
    }
    return this.http.put(
      environment.opportunityApiUrl,
      path,
      body,
      SCHEMA_VERSION.OPPORTUNITY,
      options
    );
  }

  public delete(path: string) {
    return this.http.deleteEntity(
      environment.opportunityApiUrl,
      path,
      SCHEMA_VERSION.OPPORTUNITY
    );
  }
}
